import { projectNameMap } from '@constants'
import { type CrossBrandMallHomeCampaign } from '@hooks-api/types/dataModel/cross-brand-mall'
import { Box } from '@pets-ui/Box/Box'
import { HStack } from '@pets-ui/HStack/HStack'
import { Skeleton } from '@pets-ui/Skeleton/Skeleton'
import { Tab } from '@pets-ui/Tab/Tab'
import { TabContext } from '@pets-ui/TabContext/TabContext'
import { TabList } from '@pets-ui/TabList/TabList'
import { type MobileMediaQuery } from '@pets-ui/tokens/breakpoints.stylex'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { useResponsive } from '@pets-ui/useResponsive/useResponsive'
import { VStack } from '@pets-ui/VStack/VStack'
import * as stylex from '@stylexjs/stylex'

const MOBILE: MobileMediaQuery = '@media (max-width: 480px)'

const SKELETON_TAB_COUNT_MOBILE = 4
const SKELETON_TAB_COUNT_DESKTOP = 6

const styles = stylex.create({
  tabSelected: {
    backgroundColor: colors.red50,
    color: colors.grey900,
    fontWeight: 700,
  },
  scrollContainer: {
    'scrollbarWidth': 'none',
    overflowX: { [MOBILE]: 'auto', default: 'visible' },
    '::-webkit-scrollbar': {
      display: 'none',
    },
  },
})

interface BrandTabListProps {
  campaigns: CrossBrandMallHomeCampaign[]
  activeCampaignId: number | null
  onChange: (campaignId: number) => void
  isLoading: boolean
}

export const BrandTabList = (props: BrandTabListProps) => {
  const { campaigns, activeCampaignId, onChange, isLoading } = props
  const isMobile = useResponsive('mobile')

  if (isLoading || activeCampaignId === null) {
    if (isMobile) {
      return (
        <HStack px={3} py={2} gap={2} flexWrap="nowrap" rootStyle={styles.scrollContainer}>
          {Array.from({ length: SKELETON_TAB_COUNT_MOBILE }).map((_, index) => (
            <Box key={`tab-skeleton-${index}`} flexShrink={0}>
              <Skeleton width={104} height={44} />
            </Box>
          ))}
        </HStack>
      )
    }
    return (
      <VStack p={2} gap={2} alignItems='center'>
        {Array.from({ length: SKELETON_TAB_COUNT_DESKTOP }).map((_, index) => (
          <Skeleton key={`tab-skeleton-${index}`} width={104} height={52}/>
        ))}
      </VStack>
    )
  }

  return (
    <Box rootStyle={styles.scrollContainer}>
      <TabContext value={activeCampaignId}>
        <TabList<number>
          orientation={isMobile ? 'horizontal' : 'vertical'}
          color="red"
          value={activeCampaignId}
          onChange={(_, value) => value !== undefined && onChange(value)}
        >
          {campaigns.map((campaign) => {
            const isSelected = campaign.campaign_id === activeCampaignId
            return (
              <Tab
                key={campaign.campaign_id}
                py={4}
                value={campaign.campaign_id}
                rootStyle={isSelected && styles.tabSelected}
              >
                {projectNameMap[campaign.project_code] ?? campaign.name}
              </Tab>
            )
          })}
        </TabList>
      </TabContext>
    </Box>
  )
}
